// #region imports

// --- 3rd Parth
import { cloneDeep } from "lodash";

// --- HS Code
import { CommandOfT } from "../shared/command";
import { GroupModel } from "./group-model";
import { Respondent } from "./respondent";
import { RespondentModel } from "./respondent-model";
import { RespondentStatuses } from "./respondent-statuses";

// #endregion

export class MoveRespondentCommand implements CommandOfT<GroupModel[]> {
  private _isExecuted: boolean;

  private readonly _movedRespondent: RespondentModel;
  private readonly _groups: GroupModel[];
  private readonly _originalGroupIndex: number;

  private _targetGroup: GroupModel;
  private _originalGroup: GroupModel | undefined;

  get groups(): GroupModel[] {
    if (!this._isExecuted) throw new Error('The command has not yet been executed. No changes have been affected!');
    return this._groups;
  }

  constructor(respondent: RespondentModel, target: GroupModel, groups: GroupModel[]) {
    this._isExecuted = false;

    this._groups = groups;
    this._movedRespondent = respondent;
    this._originalGroupIndex = respondent.groupIndex;
    this._targetGroup = target;
  }

  execute(): GroupModel[] {
    
    if (this._noRoom()) throw new Error('You cannot move a respondent to a group that is full');

    if (this._isExecuted) return this._groups;

    this._isExecuted = true;

    return this._moveUser();
  }

  undo(): GroupModel[] {
    if (!this._isExecuted) return this._groups;
    if (!this._originalGroup) return this._groups;

    this._targetGroup = this._originalGroup;
    this._originalGroup = this._groups.find(g => g.id.toLowerCase() === this._movedRespondent.groupId.toLowerCase());
    if (!this._originalGroup) return this._groups;

    const destinationGroupIndex = this._originalGroup.respondents.findIndex(r => r.emailAddress.toLowerCase() === this._movedRespondent.emailAddress.toLowerCase());

    this._originalGroup.respondents.splice(
      destinationGroupIndex, 1, Respondent
      .CreateEmpty(this._originalGroup.id, this._movedRespondent.groupIndex, this._originalGroup.participantId)
    );

    this._movedRespondent.groupId = this._targetGroup.id;
    this._movedRespondent.groupIndex = this._originalGroupIndex;

    this._targetGroup.respondents.pop();

    this._targetGroup.respondents.forEach(r => {
      if (r.groupIndex >= this._originalGroupIndex) r.groupIndex++;
    });

    this._targetGroup.respondents.splice(this._originalGroupIndex - 1, 0, this._movedRespondent);

    return this.groups;
  }

  private _noRoom(): boolean {
    return (this._targetGroup.respondents.filter(r => r.emailAddress.length > 0).length === this._targetGroup.respondents.length);
  }

  private _moveUser(): GroupModel[] {
    this._originalGroup = this._groups.find(g => g.id.toLowerCase() === this._movedRespondent.groupId.toLowerCase());
    if (!this._originalGroup) return this._groups;

    const respondentIndex = this._originalGroup.respondents.findIndex(r => r.emailAddress.toLowerCase() === this._movedRespondent.emailAddress.toLowerCase());
    if (respondentIndex < 0) return this._groups;

    const newGroupIndex = (this._targetGroup.respondents.find(r => r.emailAddress.length === 0)?.groupIndex);
    if (!newGroupIndex) throw new Error('You cannot move a respondent to a group that is full.');
    if (newGroupIndex > this._targetGroup.respondents.length) return this._groups;

    const currentGroupIndex = this._movedRespondent.groupIndex;
    const fillerRespondentGroupIndex = this._originalGroup.respondents.length;
    this._originalGroup.respondents.splice(respondentIndex, 1);
    this._originalGroup.respondents.forEach(r => {
      if (r.groupIndex > currentGroupIndex) r.groupIndex--;
    });
    this._originalGroup.respondents.push(Respondent.CreateEmpty(this._originalGroup.id, fillerRespondentGroupIndex, this._originalGroup.participantId));
    this._originalGroup.isDirty = true;

    this._movedRespondent.groupId = this._targetGroup.id;
    this._movedRespondent.groupIndex = newGroupIndex;
    const insertAt = this._targetGroup.respondents.findIndex(r => r.emailAddress.length === 0)
    this._targetGroup.respondents.splice(insertAt, 1, this._movedRespondent);

    const targetGroupIndex = this._groups.findIndex(g => g.id.toLowerCase() === this._targetGroup.id.toLowerCase());
    this._groups[targetGroupIndex].respondents.splice(insertAt, 1, this._movedRespondent);
    this._groups[targetGroupIndex].isDirty=true;
    return this._groups;
  }
}
